import React, {
  useEffect, useState,
} from 'react';

import {
  Box,
  Stack, Typography,
} from '@mui/material';
import dayjs from 'dayjs';

import Button from '../../../../components/Button';
import Chip from '../../../../components/Chip';
import ContentCard from '../../../../components/ContentCard';
import ContentGroupsList from '../../../../components/ContentGroupsList';
import Modal from '../../../../components/Modal';
import SearchField from '../../../../components/SearchField';
import StudentCard from '../../../../components/StudentCard';
import StudentListItem from '../../../../components/StudentListItem';
import {
  useAddToWaitlistMutation,
  useRemoveFromWaitlistMutation,
  useUpdateWaitlistMutation,
} from '../../../../services/classes';
import { Class, Waitlist } from '../../../../services/classes/types';
import { useGetStudentsQuery } from '../../../../services/students';
import { ShortStudent } from '../../../../services/students/types';
import {
  ENROLLABLE_STUDENT_STATES,
} from '../../../../utils/common/constants';
import useError from '../../../../utils/react/hooks/useError';
import useSuccess from '../../../../utils/react/hooks/useSuccess';
import { StudentsHeadingWrapper } from '../../index.styles';

interface WaitlistCardProps {
  classObj: Class;
}

const WaitlistCard = ({ classObj }: WaitlistCardProps) => {
  const [searchStr, setSearchStr] = useState('');
  const [addFormOpen, setAddFormOpen] = useState(false);
  const [addDate, setAddDate] = useState(dayjs());

  const [removeFormOpen, setRemoveFormOpen] = useState(false);

  const { data: searchedStudents, isFetching: isSearchedStudentsLoading } = useGetStudentsQuery({
    search: searchStr,
    excludeDisabled: true,
  }, { skip: searchStr === '' || !addFormOpen });

  const [addStudentToWaitlist, {
    isLoading: isAddStudentLoading,
    isError: isAddStudentError,
    error: addStudentError,
    isSuccess: isAddStudentSuccess,
  }] = useAddToWaitlistMutation();

  const [removeStudentFromWaitlist, {
    isLoading: isRemoveStudentLoading,
    isError: isRemoveStudentError,
    error: removeStudentError,
    isSuccess: isRemoveStudentSuccess,
  }] = useRemoveFromWaitlistMutation();

  const [updateWaitlist, {
    isLoading: isUpdateWaitlistLoading,
    isError: isUpdateWaitlistError,
    error: updateWaitlistError,
    isSuccess: isUpdateWaitlistSuccess,
  }] = useUpdateWaitlistMutation();

  const [selectedStudent, setSelectedStudent] = useState<ShortStudent>();

  const handleAddClose = () => {
    setAddFormOpen(false);
    setSelectedStudent(undefined);
    setAddDate(dayjs());
  };

  const handleRemoveClose = () => {
    setRemoveFormOpen(false);
    setSelectedStudent(undefined);
  };

  useEffect(() => {
    if (isAddStudentSuccess) handleAddClose();
  }, [isAddStudentSuccess]);

  useEffect(() => {
    if (isRemoveStudentSuccess) handleRemoveClose();
  }, [isRemoveStudentSuccess]);

  useError(isAddStudentError, addStudentError);
  useSuccess(isAddStudentSuccess, 'Student added to Waitlist');

  useError(isRemoveStudentError, removeStudentError);
  useSuccess(isRemoveStudentSuccess, 'Student removed from waitlist');

  useError(isUpdateWaitlistError, updateWaitlistError);
  useSuccess(isUpdateWaitlistSuccess, 'Waitlist updated');

  return (
    <ContentCard
      variant="outlined"
      title={(
        <StudentsHeadingWrapper>
          <Typography variant="h6">Waitlist</Typography>
          {classObj.waitlist.length > 0 && (
            <Chip
              label={classObj.waitlist.length}
              variant="outlined"
              size="small"
            />
          )}
        </StudentsHeadingWrapper>
      )}
    >
      <ContentGroupsList
        columns={[
          {
            field: 'order',
            headerName: 'Order',
            width: 60,
          },
          {
            field: 'name',
            headerName: 'Name',
            width: 180,
          },
          {
            field: 'school',
            headerName: 'School',
            width: 180,
          },
          {
            field: 'addedDatetime',
            headerName: 'Date Added',
            width: 120,
            renderCell: (params) => dayjs(params.value).format('DD MMM \'YY'),
          },
          {
            field: 'remove',
            headerName: 'Remove',
            width: 80,
            renderCell: (params) => (
              <Button
                color="error"
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelectedStudent(params.row as ShortStudent);
                  setRemoveFormOpen(true);
                }}
                allCaps
                variant="text"
              >
                Remove
              </Button>
            ),
          },
        ]}
        actions={{
          create: {
            fn: async () => setAddFormOpen(true),
            label: 'Add',
            size: 'small',
          },
          reorder: {
            fn: async (params) => updateWaitlist({
              subject: classObj.subject,
              classId: classObj.classId,
              waitlist: params?.orderedData.map((x: Waitlist) => x.studentId),
            }),
            label: 'Reorder',
            isSuccess: isUpdateWaitlistSuccess,
            isLoading: isUpdateWaitlistLoading,
            error: updateWaitlistError,
            size: 'small',
          },
        }}
        data={(classObj?.waitlist || []).map((w, i) => ({
          ...w,
          // order is used for displaying the position
          order: i + 1,
          // id and title are required for the reorder component
          id: w.studentId,
          title: `${w.name} (${w.school})`,
        }))}
        loading={false}
        getRowHref={(row: Waitlist) => `/admin/student/${row.studentId}`}
        getRowId={(row: Waitlist) => row.studentId}
      />
      <Modal
        open={addFormOpen}
        handleClose={handleAddClose}
        title="Add Student to Class Waitlist"
        scrollY
        buttons={
          [
            {
              label: 'Add',
              onClick: () => addStudentToWaitlist({
                subject: classObj.subject,
                classId: classObj.classId,
                studentId: selectedStudent?.studentId ?? '',
                addedDatetime: addDate.valueOf(),
              }),
              color: 'darkPrimary',
              loading: isAddStudentLoading,
              disabled: !selectedStudent,
            },
          ]
        }
      >
        <Stack direction="column" spacing={2}>
          <SearchField
            type="dropdown"
            placeholder="Search students..."
            options={searchedStudents?.data.filter(
              (s) => ENROLLABLE_STUDENT_STATES.includes(s.state) && !classObj.students.find(
                (cs) => cs.studentId === s.studentId,
              ) && !classObj.waitlist.find(
                (w) => w.studentId === s.studentId,
              ),
            )
             || []}
            getOptionKey={(s) => s.studentId}
            getOptionLabel={(s) => s.name}
            onSearch={(search) => setSearchStr(search)}
            loading={isSearchedStudentsLoading}
            onSelect={(s) => setSelectedStudent(s)}
            renderOption={(props, s) => (
              <StudentListItem
                student={s}
                {...props}
                key={s.id}
              />
            )}
            clearOnSelect
          />
          {selectedStudent ? (
            <ContentCard variant="outlined">
              <StudentCard
                student={selectedStudent}
                onClose={() => setSelectedStudent(undefined)}
                showState={false}
              />
            </ContentCard>
          ) : (
            <ContentCard variant="outlined">
              <Box padding={1}>
                <Typography variant="overline">
                  No student selected
                </Typography>
              </Box>
            </ContentCard>
          )}
        </Stack>
      </Modal>
      <Modal
        open={removeFormOpen}
        handleClose={handleRemoveClose}
        title={`Remove ${selectedStudent?.name} from Waitlist`}
        description="This student will be removed from the waitlist for this class."
        buttons={
          [
            {
              label: 'Remove',
              onClick: () => removeStudentFromWaitlist({
                subject: classObj.subject,
                classId: classObj.classId,
                studentId: selectedStudent?.studentId ?? '',
              }),
              color: 'error',
              loading: isRemoveStudentLoading,
              disabled: !selectedStudent,
            },
          ]
        }
      />
    </ContentCard>
  );
};
export default WaitlistCard;
